import React, { useState, useEffect } from "react";
import { Input, DatePicker, Select, Button, Row } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import {
  Checkbox4,
  Date4,
  DateRange4,
  Select4,
  UploadField,
  Text4,
  TextArea1,
} from "../../../../Components/FormComponents/InputFields";
import { DeleteIcon, Trash2Icon, X } from "lucide-react";

const { RangePicker } = DatePicker;

const CustomEditableCard = ({
  columnsConfig,
  additionalColumnsConfig = [],
  documentColumnsConfig = [],
  tableData,
  output,
  setOutput,
  isLoading,
  handleAddRow,
  handleRemoveRow,
  setTableData,
  allDisabled = false,
  isAddDisabled = false,
  control,
  errors,
  rowTitle,
  rowAccordion = false,
  flaggedFields = [],
  footer = null,
}) => {
  const [openAdditionalRow, setOpenAdditionalRow] = useState(null);
  const [openRowLabel, setOpenRowLabel] = useState(null);

  // ✅ SAME LOGIC AS TABLE
  useEffect(() => {
    const outputValues = Object.values(output || {});
    const requiredRows = outputValues.length;
    const currentRows = tableData.length;

    if (requiredRows > currentRows) {
      const newRows = Array.from({ length: requiredRows }, (_, index) => ({
        label: index + 1,
      }));
      setTableData(newRows);
    }
  }, [output]);

  useEffect(() => {
    if (rowAccordion && tableData.length > 0 && openRowLabel === null) {
      setOpenRowLabel(tableData[0].label);
    }
  }, [rowAccordion, tableData, openRowLabel]);

  const renderField = (col, record, index) => {
    const {
      dataIndex,
      key,
      type,
      selectOptions,
      title,
      onChange,
      valuePath,
      hiddenWhen,
      validations,
    } = col;

    const fieldId = `${dataIndex}_${record.label}`;
    const value = valuePath ? valuePath(record, output) : null;
    const fieldValidations =
      validations ||
      (dataIndex === "to_date" &&
      (index !== 0 || !output?.[record.label]?.currently_working_here)
        ? { required: true }
        : undefined);

    if (hiddenWhen?.(record, output, index)) {
      return null;
    }

    const isLocked = () => {
      if (!allDisabled) return false;
      const isFlagged = flaggedFields?.some(
        (f) => f.id === fieldId || f.id === dataIndex,
      );
      return !isFlagged;
    };

    const isDisabled = isLocked();

    if (type === "input") {
      return (
        <Text4
          key={key}
          id={fieldId}
          name={title}
          control={control}
          errors={errors}
          validations={fieldValidations}
          value={value}
          maxLength={col.maxLength}
          type={col.inputType}
          disabled={isDisabled}
          onChange={(val) => onChange(val, dataIndex, record)}
        />
      );
    }

    if (type === "textarea") {
      return (
        <TextArea1
          key={key}
          id={fieldId}
          name={title}
          value={value}
          control={control}
          errors={errors}
          validations={fieldValidations}
          disabled={isDisabled}
          onChange={(val) => onChange(val, dataIndex, record)}
        />
      );
    }

    if (type === "select") {
      return (
        <Select4
          key={key}
          id={fieldId}
          name={title}
          control={control}
          value={value}
          options={selectOptions || []}
          showSearch={col.showSearch}
          errors={errors}
          validations={fieldValidations}
          disabled={isDisabled}
          onChange={(val) => onChange(val, dataIndex, record)}
        />
      );
    }

    if (type === "datePicker") {
      return (
        <Date4
          key={key}
          id={fieldId}
          value={value}
          name={title}
          control={control}
          errors={errors}
          validations={fieldValidations}
          disabled={isDisabled}
          onChange={(val) => onChange(val, dataIndex, record)}
        />
      );
    }

    if (type === "checkbox") {
      return (
        <Checkbox4
          key={key}
          id={fieldId}
          value={value}
          name={title}
          control={control}
          errors={errors}
          validations={fieldValidations}
          disabled={isDisabled}
          onChange={(val) => onChange?.(val, dataIndex, record)}
        />
      );
    }

    if (type === "file") {
      return (
        <UploadField
          key={key}
          id={fieldId}
          name={title}
          control={control}
          errors={errors}
          validations={fieldValidations}
          disabled={isDisabled}
          onChange={(file) => onChange?.(file || null, dataIndex, record)}
          value={value}
        />
      );
    }

    return null;
  };

  return (
    <>
      {/* CARDS */}
      {tableData.map((record, index) => {
        const labelText =
          typeof rowTitle === "function"
            ? rowTitle(record, index)
            : rowTitle || `Row #${index + 1}`;

        return (
          <div
            key={record.label}
            className={`border rounded-xl p-1 mb-4 relative `}
          >
            <div
              className={`flex items-center justify-between gap-3 px-4 py-3 ${
                rowAccordion ? "cursor-pointer" : ""
              }`}
              onClick={() => {
                if (!rowAccordion) return;
                setOpenRowLabel((prev) =>
                  prev === record.label ? null : record.label,
                );
              }}
            >
              <p className="font-medium text-slate-700 m-0">{labelText}</p>
              <div className="flex items-center gap-3">
                {tableData.length > 1 && !allDisabled && (
                  <span
                    className="text-red-500 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const labelToRemove = record.label;

                      const newTableData = tableData.filter(
                        (row) => row.label !== labelToRemove,
                      );

                      if (output) {
                        const updatedOutput = { ...output };
                        delete updatedOutput[labelToRemove];
                        setOutput(updatedOutput);
                      }

                      if (handleRemoveRow) {
                        handleRemoveRow(labelToRemove);
                      }

                      setTableData(newTableData);
                    }}
                  >
                    <Trash2Icon />
                  </span>
                )}
                {rowAccordion && (
                  <span className="text-slate-500">
                    {openRowLabel === record.label ? "▾" : "▸"}
                  </span>
                )}
              </div>
            </div>

            {(!rowAccordion || openRowLabel === record.label) && (
              <>
                <Row className="align-content-start w-full">
                  {columnsConfig.map((col) => renderField(col, record, index))}
                </Row>
                {documentColumnsConfig.length > 0 && (
                  <div className="mt-3 overflow-hidden m-3">
                    <div className="border-b border-slate-200 px-2 py-1">
                      <span className="text-sm font-medium text-slate-700">
                        Document Upload <span className="text-red-500">*</span>
                      </span>
                    </div>
                    <div className="px-1 py-2">
                      <Row className="align-content-start w-full">
                        {documentColumnsConfig.map((col) =>
                          renderField(col, record, index),
                        )}
                      </Row>
                    </div>
                  </div>
                )}

                {additionalColumnsConfig.length > 0 && (
                  <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/80 m-3">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-4 py-3 text-left"
                      onClick={() =>
                        setOpenAdditionalRow((prev) =>
                          prev === record.label ? null : record.label,
                        )
                      }
                    >
                      <span className="text-sm font-medium text-slate-700">
                        Additional Details{" "}
                        <span className="text-slate-400">(Optional)</span>
                      </span>
                      <span
                        className={`transition-transform ${
                          openAdditionalRow === record.label ? "rotate-180" : ""
                        }`}
                      >
                        ▾
                      </span>
                    </button>

                    {openAdditionalRow === record.label && (
                      <div className="border-t border-slate-200 px-1 py-2">
                        <Row className="align-content-start w-full">
                          {additionalColumnsConfig.map((col) =>
                            renderField(col, record, index),
                          )}
                        </Row>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      {footer}
      <div className="flex justify-between items-center mb-4">
        <div className="flex w-full">
          <Button disabled={allDisabled || isAddDisabled} type="primary" onClick={handleAddRow}>
            + Add More
          </Button>
        </div>
      </div>
    </>
  );
};

export default CustomEditableCard;
